import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { MessageCircle, Mail, Phone, MapPin, ArrowRight } from "lucide-react";
import { Button } from "./ui/button";

const WHATSAPP_NUMBER = "528125922909";

export default function ContactSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="contact"
      ref={ref}
      className="py-24 lg:py-32 bg-[#0a0a0a] relative"
      data-testid="contact-section"
    >
      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

      <div className="max-w-5xl mx-auto px-6">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="overline mb-4">Contacto</p>
          <h2 className="font-playfair text-3xl lg:text-5xl font-bold text-white mb-6">
            ¿Comenzamos tu{" "}
            <span className="italic text-[#FF6B6B]">proyecto</span>?
          </h2>
          <p className="text-[#A3A3A3] max-w-2xl mx-auto">
            Escríbenos por WhatsApp y te responderemos de inmediato.
            ¡Estamos listos para ayudarte!
          </p>
        </motion.div>

        {/* Main WhatsApp CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="text-center mb-16"
        >
          <Button
            onClick={() => window.open(`https://wa.me/${WHATSAPP_NUMBER}`, "_blank")}
            className="bg-[#25D366] hover:bg-[#20bd5a] text-white font-manrope font-bold px-12 py-8 rounded-full flex items-center justify-center gap-4 mx-auto transition-all duration-300 hover:scale-105 text-xl whatsapp-pulse"
            data-testid="contact-whatsapp-main-btn"
          >
            <MessageCircle size={28} />
            Escríbenos por WhatsApp
            <ArrowRight size={24} />
          </Button>
          <p className="text-[#A3A3A3] mt-4 text-sm">
            Respuesta en menos de 24 horas
          </p>
        </motion.div>

        {/* Contact Info Cards */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-4"
        >
          {/* WhatsApp */}
          <a
            href={`https://wa.me/${WHATSAPP_NUMBER}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col items-center gap-3 p-6 bg-[#121212] border border-[#25D366]/30 rounded-lg hover:bg-[#25D366]/10 transition-all duration-300 group"
            data-testid="contact-whatsapp-card"
          >
            <div className="w-14 h-14 bg-[#25D366] rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
              <MessageCircle className="w-7 h-7 text-white" />
            </div>
            <div className="text-center">
              <p className="text-white font-semibold">WhatsApp</p>
              <p className="text-[#25D366] text-sm">+52 81 2592 2909</p>
            </div>
          </a>

          {/* Email */}
          <a
            href="mailto:producciones.palacios99@gmail.com"
            className="flex flex-col items-center gap-3 p-6 bg-[#121212] border border-white/10 rounded-lg hover:border-[#D4AF37]/30 transition-all duration-300 group"
            data-testid="contact-email-card"
          >
            <div className="w-14 h-14 bg-[#D4AF37]/10 rounded-full flex items-center justify-center text-[#D4AF37] group-hover:scale-110 transition-transform">
              <Mail className="w-7 h-7" />
            </div>
            <div className="text-center">
              <p className="text-white font-semibold">Email</p>
              <p className="text-[#A3A3A3] text-sm break-all">producciones.palacios99@gmail.com</p>
            </div>
          </a>

          {/* Phone */}
          <a
            href="tel:+528125922909"
            className="flex flex-col items-center gap-3 p-6 bg-[#121212] border border-white/10 rounded-lg hover:border-[#FF6B6B]/30 transition-all duration-300 group"
            data-testid="contact-phone-card"
          >
            <div className="w-14 h-14 bg-[#FF6B6B]/10 rounded-full flex items-center justify-center text-[#FF6B6B] group-hover:scale-110 transition-transform">
              <Phone className="w-7 h-7" />
            </div>
            <div className="text-center">
              <p className="text-white font-semibold">Teléfono</p>
              <p className="text-[#A3A3A3] text-sm">+52 81 2592 2909</p>
            </div>
          </a>

          {/* Location */}
          <div className="flex flex-col items-center gap-3 p-6 bg-[#121212] border border-white/10 rounded-lg">
            <div className="w-14 h-14 bg-white/5 rounded-full flex items-center justify-center text-[#A3A3A3]">
              <MapPin className="w-7 h-7" />
            </div>
            <div className="text-center">
              <p className="text-white font-semibold">Ubicación</p>
              <p className="text-[#A3A3A3] text-sm">Monterrey, N.L., México</p>
            </div>
          </div>
        </motion.div>

        {/* Social Links */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.6, duration: 0.5 }}
          className="mt-12 text-center"
        >
          <p className="text-[#A3A3A3] text-sm mb-4">Síguenos en redes sociales</p>
          <div className="flex gap-4 justify-center">
            <a
              href="https://www.facebook.com/jpalaciosproducciones/?locale=es_LA"
              target="_blank"
              rel="noopener noreferrer"
              className="w-12 h-12 bg-white/5 rounded-full flex items-center justify-center text-[#A3A3A3] hover:bg-[#D4AF37]/20 hover:text-[#D4AF37] transition-colors"
              data-testid="social-facebook"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
              </svg>
            </a>
            <a
              href="https://instagram.com"
              target="_blank"
              rel="noopener noreferrer"
              className="w-12 h-12 bg-white/5 rounded-full flex items-center justify-center text-[#A3A3A3] hover:bg-[#D4AF37]/20 hover:text-[#D4AF37] transition-colors"
              data-testid="social-instagram"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
              </svg>
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
